import Point from '@arcgis/core/geometry/Point';
import { useCallback } from 'react';
import { GEOMETRY_TYPES, SKETCH_STATES, SKETCH_EVENT_TYPES } from './constants';
import type {
  Graphic,
  SketchEvent,
  GraphicsClickEvent,
  UseSketchHelpersProps,
} from './types';
import {
  calculateAndSetArea,
  createDistanceLabels,
  createTextSymbol,
  createPolygonSymbol,
  createPointSymbol,
  getGeometryCenter,
  shouldCreateDistanceLabels,
} from './utils';

const initialAttributes = {
  note: '',
  boundaryColor: '',
  polygonColor: '',
  lastEdited: new Date().toISOString(),
};

export const useSketchHelpers = ({
  graphicsLayer,
  notePopover,
  setNotePopover,
  updateGraphicLayer,
}: UseSketchHelpersProps) => {
  const updateTextSymbols = useCallback(() => {
    if (!graphicsLayer) return;

    const textGraphicsToAdd: Graphic[] = [];

    graphicsLayer.graphics.forEach((graphic: Graphic) => {
      if (graphic.attributes.isDistanceLabel) return;

      const note = graphic.attributes.note;
      const geometry = graphic.geometry;
      const shouldShowThisLabel =
        note || geometry.type === GEOMETRY_TYPES.POLYGON;

      if (shouldShowThisLabel) {
        try {
          const center = getGeometryCenter(geometry);
          if (!center) return;

          calculateAndSetArea(graphic);

          let displayText = '';
          if (graphic.attributes.area) {
            displayText = `Area: ${graphic.attributes.area}`;
          }
          if (note?.trim()) {
            displayText = displayText ? `${displayText}\n${note}` : note;
          }

          const textPoint = new Point({
            x: center.x,
            y: center.y,
            spatialReference: geometry.spatialReference,
          });

          if (geometry.type === GEOMETRY_TYPES.POLYGON) {
            graphic.symbol = createPolygonSymbol(
              graphic.attributes.boundaryColor,
              graphic.attributes.polygonColor,
            );
          } else if (geometry.type === GEOMETRY_TYPES.POINT) {
            graphic.symbol = createPointSymbol(
              graphic.attributes.boundaryColor,
              graphic.attributes.polygonColor,
            );
          }

          const textGraphic: Graphic = {
            geometry: textPoint,
            symbol: createTextSymbol(displayText),
            attributes: {
              ...graphic.attributes,
              isTextLabel: true,
              parentId: graphic.attributes.OBJECTID,
            },
          };

          const existingTextGraphic = graphicsLayer.graphics.find(
            (g: Graphic) =>
              g.attributes.isTextLabel === true &&
              g.attributes.parentId === graphic.attributes.OBJECTID,
          );
          if (existingTextGraphic) {
            graphicsLayer.remove(existingTextGraphic);
          }

          textGraphicsToAdd.push(textGraphic);
        } catch (error) {
          // fail silently
        }
      } else if (geometry.type === GEOMETRY_TYPES.POLYGON) {
        graphic.symbol = createPolygonSymbol(
          graphic.attributes.boundaryColor,
          graphic.attributes.polygonColor,
        );

        const existingTextGraphic = graphicsLayer.graphics.find(
          (g: Graphic) =>
            !!g.attributes.isTextLabel &&
            g.attributes.parentId === graphic.attributes.OBJECTID,
        );
        if (existingTextGraphic) {
          graphicsLayer.remove(existingTextGraphic);
        }
      } else if (geometry.type === GEOMETRY_TYPES.POINT) {
        graphic.symbol = createPointSymbol(
          graphic.attributes.boundaryColor,
          graphic.attributes.polygonColor,
        );

        const existingTextGraphic = graphicsLayer.graphics.find(
          (g: Graphic) =>
            !!g.attributes.isTextLabel &&
            g.attributes.parentId === graphic.attributes.OBJECTID,
        );
        if (existingTextGraphic) {
          graphicsLayer.remove(existingTextGraphic);
        }
      }
    });

    textGraphicsToAdd.forEach(textGraphic => {
      graphicsLayer.add(textGraphic);
    });

    // Manual save will be triggered when user clicks Save button
  }, [graphicsLayer]);

  const updateGraphicNoteLive = useCallback(
    (note: string, boundaryColor?: string, polygonColor?: string) => {
      if (notePopover?.graphic && graphicsLayer) {
        const graphic = notePopover.graphic;
        // Create new attributes object to avoid read-only property errors
        graphic.attributes = {
          ...graphic.attributes,
          note: note,
          boundaryColor: boundaryColor || '',
          polygonColor: polygonColor || '',
          lastEdited: new Date().toISOString(),
        };

        if (graphic.geometry?.type === GEOMETRY_TYPES.POLYGON) {
          graphic.symbol = createPolygonSymbol(
            boundaryColor || '',
            polygonColor || '',
          );
        } else if (graphic.geometry?.type === GEOMETRY_TYPES.POINT) {
          graphic.symbol = createPointSymbol(
            boundaryColor || '',
            polygonColor || '',
          );
        }

        calculateAndSetArea(graphic);

        const hasContent = (note && note.trim()) || graphic.attributes.area;
        if (hasContent) {
          const existingTextGraphic = graphicsLayer.graphics.find(
            (g: Graphic) =>
              !!g.attributes.isTextLabel &&
              g.attributes.parentId === graphic.attributes.OBJECTID,
          );
          if (existingTextGraphic) {
            graphicsLayer.remove(existingTextGraphic);
          }

          const center = getGeometryCenter(graphic.geometry);
          if (center) {
            const textPoint = new Point({
              x: center.x,
              y: center.y,
              spatialReference: graphic.geometry.spatialReference,
            });

            let displayText = '';
            if (graphic.attributes.area) {
              displayText = `Area: ${graphic.attributes.area}`;
            }
            if (note?.trim()) {
              displayText = displayText ? `${displayText}\n${note}` : note;
            }

            const textGraphic: Graphic = {
              geometry: textPoint,
              symbol: createTextSymbol(displayText),
              attributes: {
                ...graphic.attributes,
                isTextLabel: true,
                parentId: graphic.attributes.OBJECTID,
              },
            };

            graphicsLayer.add(textGraphic);
          }
        } else {
          const existingTextGraphic = graphicsLayer.graphics.find(
            (g: Graphic) =>
              !!g.attributes.isTextLabel &&
              g.attributes.parentId === graphic.attributes.OBJECTID,
          );
          if (existingTextGraphic) {
            graphicsLayer.remove(existingTextGraphic);
          }
        }
      }
    },
    [notePopover, graphicsLayer],
  );

  const handleClosePopover = useCallback(() => {
    setNotePopover(null);
    updateGraphicLayer();
  }, [setNotePopover, updateGraphicLayer]);

  const handleSketchComplete = useCallback(
    (event: SketchEvent) => {
      if (event.state === SKETCH_STATES.COMPLETE) {
        if (event.type === SKETCH_EVENT_TYPES.CREATE) {
          const graphic =
            event.graphic || (event.graphics && event.graphics[0]);
          if (graphic) {
            graphic.attributes = {
              ...graphic.attributes,
              OBJECTID: Date.now(),
              ...initialAttributes,
            };

            if (event.tool === GEOMETRY_TYPES.POINT) {
              graphic.symbol = createPointSymbol('', '');
            } else if (graphic.geometry?.type === GEOMETRY_TYPES.POLYGON) {
              graphic.symbol = createPolygonSymbol('', '');
            }

            setNotePopover({
              graphic,
              ...initialAttributes,
            });
          }

          // Redux persistence will be handled when user clicks "Save" in note popover
        }

        updateTextSymbols();
      } else if (event.state === SKETCH_STATES.ACTIVE) {
        if (
          event.graphic &&
          graphicsLayer &&
          shouldCreateDistanceLabels(event, graphicsLayer)
        ) {
          createDistanceLabels(event.graphic, graphicsLayer);
        }
        updateTextSymbols();
      } else if (event.state === SKETCH_STATES.CANCEL) {
        if (event.tool === GEOMETRY_TYPES.POLYLINE && graphicsLayer) {
          const distanceLabels: Graphic[] = [];
          graphicsLayer.graphics.forEach((g: Graphic) => {
            if (g.attributes.isDistanceLabel) {
              distanceLabels.push(g);
            }
          });
          distanceLabels.forEach((label: Graphic) => {
            graphicsLayer.remove(label);
          });
        }
      } else if (event.type === SKETCH_EVENT_TYPES.DELETE) {
        const graphic = event.graphic || (event.graphics && event.graphics[0]);

        if (graphic && graphic.attributes.OBJECTID && graphicsLayer) {
          const textGraphic = graphicsLayer.graphics.find(
            (g: Graphic) =>
              !!g.attributes.isTextLabel &&
              g.attributes.parentId === graphic.attributes.OBJECTID,
          );
          if (textGraphic) {
            graphicsLayer.remove(textGraphic);
          }
          setNotePopover(null);

          // Redux persistence will be handled when user clicks "Save" in note popover
        }
        updateGraphicLayer();
      }
    },
    [graphicsLayer, setNotePopover, updateTextSymbols, updateGraphicLayer],
  );

  const handleGraphicsClick = useCallback(
    (event: GraphicsClickEvent) => {
      if (
        event.graphic &&
        (event.graphic?.geometry?.type === GEOMETRY_TYPES.POLYGON ||
          event.graphic?.geometry?.type === GEOMETRY_TYPES.POINT) &&
        graphicsLayer
      ) {
        setNotePopover({
          graphic: event.graphic,
          note: event.graphic.attributes.note || '',
          boundaryColor: event.graphic.attributes.boundaryColor || '',
          polygonColor: event.graphic.attributes.polygonColor || '',
        });
      }
    },
    [graphicsLayer, setNotePopover],
  );

  const handleNotePopoverSubmit = useCallback(
    (note: string, boundaryColor?: string, polygonColor?: string) => {
      if (notePopover?.graphic && graphicsLayer) {
        const polygonGraphic = graphicsLayer.graphics.find(
          (g: Graphic) =>
            g.attributes.OBJECTID === notePopover.graphic.attributes.OBJECTID,
        );

        if (polygonGraphic) {
          // Update the graphic with new attributes and symbol
          // Redux persistence is handled in EsriSketchGL handleSubmit after this completes
          polygonGraphic.attributes = {
            ...polygonGraphic.attributes,
            note,
            boundaryColor,
            polygonColor,
            lastEdited: new Date().toISOString(),
          };

          polygonGraphic.symbol = createPolygonSymbol(
            boundaryColor,
            polygonColor,
          );

          updateTextSymbols();
        }
      }

      setNotePopover(null);
    },
    [notePopover, graphicsLayer, updateTextSymbols, setNotePopover],
  );

  return {
    handleClosePopover,
    handleSketchComplete,
    handleGraphicsClick,
    updateTextSymbols,
    handleNotePopoverSubmit,
    updateGraphicNoteLive,
  };
};
